import BaseImage from '@unifyapps/carbon/components/Image';
import { useBlockAppearanceStyles } from '@unifyapps/carbon/no-code/hooks/useBlockAppearanceStyles';
import type { StyleObject } from '@unifyapps/defs/types/styleObject';
import { Box } from '@unifyapps/ui/components/Box';
import { clsx } from 'clsx';
import type { SyntheticEvent } from 'react';
import { forwardRef } from 'react';

type Props = {
  onClick?: (e: SyntheticEvent) => void;
  onError?: (e: SyntheticEvent<HTMLImageElement>) => void;
  attributesAndEventListeners?: Record<string, unknown>;
  hasClickEvent?: boolean;
  src?: string;
  alt?: string;
  styles?: Pick<StyleObject, 'padding' | 'margin' | 'borderRadius' | 'height' | 'width'>;
  objectFit?:
    | 'object-contain'
    | 'object-cover'
    | 'object-fill'
    | 'object-none'
    | 'object-scale-down'
    | undefined;
  className?: string;
};

export const Image = forwardRef<HTMLImageElement, Props>(
  (
    {
      attributesAndEventListeners,
      hasClickEvent,
      onClick,
      onError,
      src,
      alt,
      styles,
      objectFit,
      className,
      ...props
    },
    ref,
  ) => {
    const { className: blockAppearanceClassName, style } = useBlockAppearanceStyles({
      appearanceStyles: styles,
    });

    return (
      <Box
        {...attributesAndEventListeners}
        className={clsx('overflow-hidden', blockAppearanceClassName, className)}
        enableKeyboardActivation={Boolean(hasClickEvent)}
        onClick={onClick}
        ref={ref}
        style={style}
        {...props}
      >
        <BaseImage
          alt={alt}
          className={clsx('h-full w-full', objectFit, {
            'cursor-pointer': hasClickEvent,
          })}
          onError={onError}
          src={src}
        />
      </Box>
    );
  },
);
